/**
 * Boss battles — the culmination of a kingdom's mathematics.
 *
 * `docs/rpg.md` §15 asks for a staged fight that "tests mastery rather than
 * simple memorization" and explicitly refuses the obvious implementation:
 *
 *   > Do not make boss battles simply: 20 random questions.
 *
 * So a boss is stages, and a stage is a named piece of the kingdom's own
 * mathematics asked at one difficulty. The Number Dragon's scales are place
 * value, its hoard is decimals and its flame is the primes; passing means
 * getting three of four right at that difficulty, not surviving a war of
 * attrition. Like a quest, a boss holds no mathematics of its own — every
 * question is generated from the same templates the practice page uses, so a
 * student who can do the kingdom can do the boss, and nothing here can ask
 * something the curriculum never taught.
 *
 * **The fight itself lives in `encounter.ts` now.** That file is this one with
 * the kingdom taken out: the state machine, the seeding, the scoring and the
 * three rules below are shared with every smaller fight in the world, because
 * two state machines over the same battle would drift on exactly the questions
 * that are hard to get right — what a malformed answer does, whether a failed
 * stage ends the attempt, whether a retreat is a loss. What is left here is
 * what is genuinely about a *boss*: the story, the reward, the claim, and the
 * fact that a ceremony is fought at a fixed difficulty rather than one that
 * moves with the student. Every name this file exported before it was split
 * still comes out of it, meaning exactly what it meant.
 *
 * Four rules shape the rest, and three of them are `CLAUDE.md` invariants:
 *
 * **The battle is derived, not stored.** `startBattle` is a pure function of
 * (definition, learner, attempt) and rebuilds the identical fight every time it
 * is called — see the determinism note on `startEncounter`. Nothing about a
 * battle in progress needs persisting, and a boss page that is reloaded,
 * restored from a background tab or re-rendered by React shows the same
 * questions it showed a second ago.
 *
 * **Malformed input is not a wrong answer.** An empty box, "half" or `3/0`
 * changes nothing at all: not the score, not the question index, not the list
 * of concepts to review. A boss is exactly where a student is most likely to
 * fumble the keyboard, and losing a dragon fight to a typo is the single most
 * effective way to teach somebody to fear the answer box.
 *
 * **Losing costs nothing** (`docs/rpg.md` §12, `docs/rpg-implementation-plan.md`
 * §0.6). There is no HP, no gold penalty, no lost streak and no stored record
 * of a failed attempt anywhere in the profile. A defeat produces exactly one
 * artefact — the list of concepts the wrong answers touched — and the retry is
 * the next `attemptIndex`, with fresh problems.
 *
 * **The mathematics still counts.** Every graded boss answer must *also* go
 * through `applyAttempt`; see the integration note at the bottom of this file.
 * A question answered in a boss fight is a question answered.
 */

import type { ExerciseBank } from '@yuan-math/exercises';
import {
  claimQuestReward,
  recordBossClear,
  type BossAnswer,
  type LearnerProfile,
} from '@yuan-math/learning';
import {
  encounterConceptIds,
  encounterReadiness,
  replayEncounter,
  startEncounter,
  type EncounterDef,
  type EncounterRun,
  type EncounterStage,
  type EncounterState,
} from './encounter.ts';
import type { QuestReward } from './quests.ts';

/*
 * The shared engine, under the names a boss page has always called it by.
 * Re-exported rather than wrapped: a wrapper would be a second place for the
 * behaviour to be described, and the whole point of the split is that there is
 * only one.
 */
export {
  conceptsToReview,
  currentQuestion,
  encounterReducer as battleReducer,
  STAGE_PASS_SHARE,
} from './encounter.ts';
export type {
  EncounterEvent as BattleEvent,
  EncounterOutcome as BattleOutcome,
  EncounterQuestion as BattleQuestion,
  EncounterState as BattleState,
  EncounterStageState as BattleStage,
} from './encounter.ts';

/* -------------------------------------------------------------------------- */
/* The definition                                                             */
/* -------------------------------------------------------------------------- */

/**
 * One phase of the fight: a named slice of the kingdom's mathematics.
 *
 * `conceptIds` are dealt round-robin across the stage's questions, so a stage
 * of four questions over two concepts asks two of each — a stage is a *theme*,
 * not a single skill, and "Scales of Ten" that turned out to be four rounding
 * questions would be a difficulty band wearing a name.
 *
 * A boss stage is always `fixed`: every question comes at the stated band, and
 * the bands climb between stages. The engine can also move the band with the
 * student, and a boss deliberately does not — a ceremony should be the same
 * mountain for everyone who walks up to it, and a dragon that quietly got
 * easier because you were struggling would make the victory worth less than the
 * student thinks it is.
 */
export type BossStage = Omit<EncounterStage, 'banding'>;

/**
 * What the boss says. Short lines, skippable, and data rather than components —
 * the same rule that keeps lesson prose out of `apps/web` (`CLAUDE.md`).
 *
 * `defeat` is written to be read by a student who has just lost, so §12's tone
 * is a content requirement rather than a suggestion: it names what to go and
 * look at, and it never says the student is bad at mathematics.
 */
export type BossStory = {
  intro: string[];
  victory: string[];
  defeat: string[];
};

export type BossDef = {
  id: string;
  kingdomId: string;
  /** "The Number Dragon". */
  name: string;
  emoji: string;
  story: BossStory;
  /** One to three. §15's dragon has three; a village gate trial has two. */
  stages: BossStage[];
  /**
   * Paid once, on the first clear, through the same idempotent claim a quest
   * reward goes through. Same currencies and the same economy (§0.2) — a boss
   * pays more than a quest and nothing else about it is different.
   */
  reward: QuestReward;
};

/** Boss reward bands. A boss is worth several quests and never a whole level. */
export const BOSS_XP_MIN = 150;
export const BOSS_XP_MAX = 300;
export const BOSS_GOLD_MIN = 40;
export const BOSS_GOLD_MAX = 80;

/* -------------------------------------------------------------------------- */
/* The battle                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * A boss, as the shared engine sees it: stages at fixed difficulty, and the
 * standard three-quarters pass share.
 *
 * The `banding` is stated rather than left to the default, because it is a
 * decision about what a boss *is* (see `BossStage`) and not a value that
 * happens to be convenient.
 */
function runFor(def: BossDef, bank: ExerciseBank, learnerId: string, attemptIndex: number): EncounterRun {
  const encounter: EncounterDef = {
    id: def.id,
    stages: def.stages.map((stage) => ({ ...stage, banding: 'fixed' as const })),
  };
  return { def: encounter, bank, learnerId, attemptIndex };
}

/**
 * Builds the fight.
 *
 * **The invariant that makes this design work: same inputs, same battle** —
 * stated in full on `startEncounter`, which is what this calls. A retry is
 * `attemptIndex + 1` and therefore a genuinely different fight: beating the
 * Number Dragon by memorising four answers is not beating it.
 *
 * `now` is injected, like everywhere below `apps/web`, and is recorded rather
 * than used: the seed deliberately does *not* depend on it, because a seed that
 * moved with the clock is exactly the reload bug this design exists to avoid.
 *
 * `bank` is a parameter for the reason the quest catalogue is: `packages/world`
 * sits below `content/`, so it cannot import the bank the app actually uses.
 */
export function startBattle(
  def: BossDef,
  bank: ExerciseBank,
  learnerId: string,
  attemptIndex: number,
  now: number,
): EncounterState {
  return startEncounter(runFor(def, bank, learnerId, attemptIndex), now);
}

/**
 * Rebuilds a fight from the answers the profile kept — the reason a battle in
 * progress can be persisted in two numbers. See `replayEncounter`.
 */
export function replayBattle(
  def: BossDef,
  bank: ExerciseBank,
  learnerId: string,
  attemptIndex: number,
  answers: readonly BossAnswer[],
  now: number,
): EncounterState {
  return replayEncounter(runFor(def, bank, learnerId, attemptIndex), answers, now);
}

/**
 * Every concept the boss tests, once each, in stage order.
 *
 * What a readiness line is counted against: "you know 7 of the 9 skills the
 * Dragon tests" is this list intersected with the student's `knownSet`.
 */
export function bossConceptIds(def: BossDef): string[] {
  return encounterConceptIds({ id: def.id, stages: def.stages });
}

/**
 * How ready a student is for the fight — as a count and a list, never as a
 * verdict. See `encounterReadiness`: there is no `ready: boolean` here and no
 * threshold anywhere, because a boss gate states what the boss will ask and
 * then opens.
 */
export function bossReadiness(
  def: BossDef,
  known: ReadonlySet<string>,
): { known: number; total: number; unknownConceptIds: string[] } {
  return encounterReadiness({ id: def.id, stages: def.stages }, known);
}

/* -------------------------------------------------------------------------- */
/* Clearing                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The claim id a boss reward is paid under.
 *
 * Boss rewards share `claimedQuestIds` with quest rewards, because they are the
 * same settlement and `claimQuestReward` is already idempotent by construction.
 * The `boss:` prefix is what keeps the two namespaces from ever meeting; a
 * quest id may not start with it, and `validateQuests` says so.
 */
export const bossClaimId = (bossId: string): string => `boss:${bossId}`;

/** Whether this boss is already down. A cleared boss stays cleared. */
export function bossCleared(def: BossDef, profile: LearnerProfile): boolean {
  return profile.rpg?.clearedBossIds.includes(def.id) ?? false;
}

/**
 * Records a win and pays for it, once.
 *
 * Two reducers, both already idempotent: `recordBossClear` is what the world
 * map reads to draw the kingdom cleared and the next seal open, and
 * `claimQuestReward` under `boss:<id>` is what pays the XP and gold. A boss
 * fought again for the fun of it is recorded again (a no-op) and paid nothing,
 * which is the point of putting the settlement here rather than in the UI.
 *
 * Anything other than a win returns the profile untouched — losing has no
 * consequence anywhere in the profile, and neither does retreating.
 */
export function settleBossVictory(
  profile: LearnerProfile,
  def: BossDef,
  state: EncounterState,
): LearnerProfile {
  if (state.defId !== def.id || state.outcome !== 'won') return profile;
  const cleared = recordBossClear(profile, { bossId: def.id });
  return claimQuestReward(cleared, {
    questId: bossClaimId(def.id),
    xp: def.reward.xp,
    gold: def.reward.gold,
  });
}

/* -------------------------------------------------------------------------- */
/* Integration note — for every page that runs a fight                        */
/* -------------------------------------------------------------------------- */

/*
 * **Every graded answer must also go through `applyAttempt`.**
 *
 * A boss question is generated from the ordinary exercise bank and graded by
 * the ordinary grader, so it is real evidence about what the student can do.
 * Feeding it to the battle reducer alone would mean a student could fight the
 * Number Dragon for ten minutes and have their mastery, their streak, their
 * XP, their daily quests and their attempt log all record that they did
 * nothing. Mastery is earned by practice (`CLAUDE.md` §1), and this is
 * practice. So the boss page calls both, in this order, per answer:
 *
 *     const graded = grade(question.exercise, response);
 *     const outcome = applyAttempt(profile, { conceptId: question.conceptId, ... , result: graded, now });
 *     setBattle(battleReducer(battle, { kind: 'answer', gradeResult: graded }));
 *
 * `applyAttempt` already refuses malformed input, and so does the reducer, so
 * the two stay in step on a typo without either knowing about the other.
 *
 * This is a rule about *fights*, not about bosses: an encounter at any scale
 * owes the same debt, and `encounter.ts` restates it at the top of the file.
 *
 * **The review ladder is safe, and this is why.** `CLAUDE.md` warns that the
 * ladder advances once per *session*, not once per problem — twelve correct
 * answers in one sitting is one successful recall, and advancing twelve times
 * would push the next review out by months. A boss is a multi-question sitting
 * on a handful of concepts, so it is precisely the shape that would break it.
 *
 * It does not, and no new code is needed, because `applyAttempt` already
 * guards it:
 *
 *     const dueNow = previous.dueAt === undefined || previous.dueAt <= now;
 *     const scheduled = !correct || dueNow ? { ...state, ...scheduleNext(...) } : state;
 *
 * The first correct answer on a concept schedules a review a day or more out;
 * every later correct answer in the same sitting finds `dueAt` in the future
 * and leaves the schedule alone. A wrong answer always reschedules, because a
 * concept the student just missed should come back tomorrow — which is exactly
 * what a losing boss stage should do. `boss.test.ts` proves this against the
 * real reducer rather than trusting the comment.
 */
